//! Mutual exclusivity for the panels rendered to the right of the grid (see
//! QuadraticUI.tsx): code editor, cell edit history, validations, conditional
//! formatting, scheduled tasks, and file history. Only one may be open at a
//! time.
//!
//! Each panel's atom module registers a closer function here at module load,
//! and its atom setter calls `closeRightSidePanels` when the panel transitions
//! from closed to open. Registered closers must write to their module's base
//! atom (or an equivalent side-effect-safe close path), never back through the
//! derived atom whose setter triggers exclusivity — that keeps the mechanism
//! non-reentrant and avoids import cycles between the atom modules.

export type RightSidePanel =
  | 'codeEditor'
  | 'cellEditHistory'
  | 'validation'
  | 'conditionalFormat'
  | 'scheduledTasks'
  | 'fileHistory'
  | 'findReplace';

const closers = new Map<RightSidePanel, () => void>();

export function registerRightSidePanelCloser(panel: RightSidePanel, close: () => void) {
  closers.set(panel, close);
}

/**
 * Closes every registered right-side panel except the one(s) being opened.
 * Closers are idempotent, so panels that are already closed are no-ops.
 */
export function closeRightSidePanels(except: RightSidePanel | RightSidePanel[]) {
  const keep = new Set(Array.isArray(except) ? except : [except]);
  const seen = new Set<() => void>();
  for (const [panel, close] of closers) {
    if (keep.has(panel) || seen.has(close)) continue;
    seen.add(close);
    close();
  }
}
